import { useMemo, useState } from 'react';
import { PermissionEnum, useQueryState } from '@ui/src';
import { agreementsListSchema } from '../variables/agreements';
import { ApplicationAgreement } from '../../../../../hoc/new-application/types';
import { useLazyGetCustomersAgreementFileQuery } from '../../../../../hoc/new-application/store/services';
import getAgreementTypeName from '../../../../../hoc/new-application/helpers/get-agreement-type-name';
import formatAgreementsList from '../helpers/format-agreements-list';

const useAgreements = ({
  list,
  activePermissions,
}: {
  list: ApplicationAgreement[];
  activePermissions?: PermissionEnum[];
}) => {
  const [downloadFile, setDownloadedFile] = useState('');

  const { filters, sort, onSortChange, onFilterChange } =
    useQueryState(agreementsListSchema);

  const [
    getFile,
    { isLoading: fileDownloadInProgress, isFetching: fileDownloadIsFetching },
  ] = useLazyGetCustomersAgreementFileQuery();

  const getAgreementFile = async (id: string) => {
    setDownloadedFile(id);

    const response = await getFile(id);

    if (response.data) {
      const currentAgreement = list?.find((item) => item.id === id);

      if (currentAgreement) {
        const hiddenElement = document.createElement('a');
        const url = window.URL || window.webkitURL;

        // @ts-ignore
        const blob = url.createObjectURL(response.data);
        hiddenElement.href = blob;
        hiddenElement.target = '_blank';
        hiddenElement.download = `${getAgreementTypeName(
          currentAgreement?.type,
        )}(v${currentAgreement?.version})`;
        hiddenElement.click();

        setDownloadedFile('');
      }
    }
  };

  const formattedData = useMemo(() => {
    const data = formatAgreementsList(
      list,
      getAgreementFile,
      downloadFile,
      fileDownloadInProgress || fileDownloadIsFetching,
      activePermissions,
    );

    return data;
  }, [
    list,
    downloadFile,
    fileDownloadInProgress,
    fileDownloadIsFetching,
    getAgreementFile,
    activePermissions,
  ]);

  return {
    formattedData,
    sort,
    filters,
    onSortChange,
    onFilterChange,
  };
};

export default useAgreements;
